// frontend/src/App.js
// ✅ Aplicación principal con autenticación y rutas

import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import PrivateRoute from './utils/PrivateRoute';

// Layouts
import Admin from './layouts/Admin';

// Auth Pages
import Login from './pages/Auth/Login';
import PendingApproval from './pages/Auth/PendingApproval';

/**
 * Componente interno que maneja las rutas
 * Debe estar dentro de AuthProvider para usar useAuth
 */
function AppRoutes() {
  const { loading, isAuthenticated } = useAuth();

  // ✅ Configurar clases del body al montar
  useEffect(() => {
    document.body.classList.add('g-sidenav-show', 'bg-gray-100');
    
    return () => {
      document.body.classList.remove('g-sidenav-show', 'bg-gray-100');
    };
  }, []);

  // ✅ Mostrar spinner mientras carga la autenticación
  if (loading) {
    return (
      <div 
        className="d-flex justify-content-center align-items-center" 
        style={{ height: '100vh', backgroundColor: '#f8f9fa' }}
      >
        <div className="text-center">
          <div 
            className="spinner-border text-primary" 
            role="status" 
            style={{ width: '3rem', height: '3rem' }}
          >
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-3 text-muted">Verificando sesión...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* ========================================
          RUTAS PÚBLICAS
      ======================================== */}
      
      {/* Login - Redirige a dashboard si ya está autenticado */}
      <Route 
        path="/login" 
        element={
          isAuthenticated ? (
            <Navigate to="/admin/dashboard" replace />
          ) : (
            <Login />
          )
        } 
      />
      
      {/* Pending Approval - Página de espera */}
      <Route 
        path="/pending-approval" 
        element={<PendingApproval />} 
      />

      {/* ========================================
          RUTAS PROTEGIDAS
      ======================================== */}
      
      {/* Admin Layout - Requiere autenticación */}
      <Route
        path="/admin/*"
        element={
          <PrivateRoute>
            <Admin />
          </PrivateRoute>
        }
      />

      {/* ========================================
          REDIRECTS
      ======================================== */}
      
      {/* Raíz - Redirige según estado de autenticación */}
      <Route 
        path="/" 
        element={
          <Navigate 
            to={isAuthenticated ? "/admin/dashboard" : "/login"} 
            replace 
          />
        } 
      />
      
      {/* Catch-all - Redirige rutas no encontradas */}
      <Route 
        path="*" 
        element={
          <Navigate 
            to={isAuthenticated ? "/admin/dashboard" : "/login"} 
            replace 
          />
        } 
      />
    </Routes>
  );
}

/**
 * Componente principal de la aplicación
 * Envuelve todo con AuthProvider
 */
function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
